<script setup lang="tsx">
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { commonProps } from './props'
import ClearButton from './ClearButton.vue'
import Multiselect from 'vue-multiselect'
import type { User, Response } from '@/models/api-generated'
import http from '@/services/http'
import type { Column } from '@/stores/column'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()
const isLoading = ref(false)
const isOpen = ref(false)
const model = defineModel<Column>({ required: true })
const props = defineProps(commonProps)
const options = ref<User[]>([])

const internalModel = computed<User[]>({
	get() {
		return model.value.filter.map((item: any) => {
			const matchingOption = options.value.find((opt) => opt.id === item)
			return matchingOption ? { id: matchingOption.id, label: matchingOption.label } : { id: item, label: item }
		})
	},
	set(value: User[]) {
		model.value.filter = value.map((x) => x.id)
	},
})

const responsibleIcon = (option: User) => {
	if (option.active === 0) {
		return 'bi-person-fill me-2 pie3'
	} else if (option.active === 1) {
		return 'bi-person-fill me-2 pie1'
	}
	return ''
}

onBeforeUnmount(() => (model.value.filter = []))
onBeforeMount(() => search(''))

async function search(q: string) {
	isLoading.value = true

	const { data } = await http.get<Response<User[]>>('api/users?q=' + q)

	// @ts-ignore
	data.data?.push({
		id: undefined as any,
		label: i18n.t('misc.nothing'),
		firstname: '',
		lastname: '',
	})

	options.value = data.data ?? []
	isLoading.value = false
}
</script>

<template>
	<Multiselect
		:multiple="true"
		:show-labels="false"
		:hide-selected="true"
		:close-on-select="false"
		:clear-on-select="false"
		:id="props.id"
		:disabled="props.disabled"
		:placeholder="model.label + '...'"
		:options="options"
		:internal-search="false"
		:preserve-search="true"
		:show-no-results="false"
		:loading="isLoading"
		v-model="internalModel"
		label="label"
		track-by="id"
		:limit="isOpen ? 100 : 3"
		@search-change="search"
		@open="isOpen = true"
		@close="isOpen = false"
	>
		<template #clear>
			<ClearButton right="2rem" v-if="internalModel.length" @click="internalModel = []"></ClearButton>
		</template>

		<template v-if="!isOpen" #limit>
			<strong class="multiselect__strong">{{ $t('misc.limit', [internalModel.length - 3]) }}</strong>
		</template>

		<template #option="{ option }">
			<div><i :class="responsibleIcon(option)"></i>{{ option.label }}</div>
		</template>
	</Multiselect>
</template>
